import { useState } from 'react';
import {
  Button,
  Card,
  ChipToggle,
  ErrorState,
  Field,
  Input,
  LoadingState,
  useToast,
} from '@/components/ui';
import { parseRM, senToInput } from '@/features/billing/money';
import {
  useDeliverySettings,
  useSaveDeliverySettings,
} from '@/features/deliveries/api';
import type { DeliverySettings } from '@/features/deliveries/types';
import { isApiError } from '@/lib/api';
import styles from '@/pages/queue/queue.module.css';
import own from './DeliverySettingsCard.module.css';

const toTime = (min: number) =>
  `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
const toMin = (hhmm: string) => {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/**
 * RL-1655 (gap #96): same-day medicine delivery under Services & prices: the
 * fee (empty = delivery off), the branches that deliver, the cut-off for
 * asking and the time deliveries arrive by.
 */
export function DeliverySettingsCard({ can }: { can: boolean }) {
  const q = useDeliverySettings();
  return (
    <Card title="Medicine delivery">
      {q.isLoading ? (
        <LoadingState label="Medicine delivery" />
      ) : q.error || !q.data ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <DeliveryForm
          key={JSON.stringify(q.data)}
          can={can}
          settings={q.data}
        />
      )}
    </Card>
  );
}

function DeliveryForm({
  can,
  settings,
}: {
  can: boolean;
  settings: DeliverySettings;
}) {
  const save = useSaveDeliverySettings();
  const toast = useToast();
  const [fee, setFee] = useState(
    settings.feeSen === null ? '' : senToInput(settings.feeSen),
  );
  const [branchIds, setBranchIds] = useState(settings.branchIds);
  const [cutoff, setCutoff] = useState(toTime(settings.cutoffMin));
  const [deliverBy, setDeliverBy] = useState(toTime(settings.deliverByMin));
  const submit = () => {
    const feeSen = fee.trim() ? parseRM(fee) : null;
    const cutoffMin = toMin(cutoff);
    const deliverByMin = toMin(deliverBy);
    if (fee.trim() && feeSen === null)
      return toast.show({ tone: 'error', title: 'Check the fee' });
    if (
      cutoffMin === null ||
      deliverByMin === null ||
      deliverByMin <= cutoffMin
    )
      return toast.show({
        tone: 'error',
        title: 'Deliveries arrive after the cut-off time',
      });
    void save
      .mutateAsync({ feeSen, branchIds, cutoffMin, deliverByMin })
      .then(() =>
        toast.show({ tone: 'success', title: 'Delivery settings saved' }),
      )
      .catch((err: unknown) =>
        toast.show({
          tone: 'error',
          title: 'Not saved',
          body: isApiError(err) ? err.message : undefined,
          error: err,
        }),
      );
  };
  return (
    <div className={own.form}>
      <p className={styles.sub}>
        Patients ask for same-day delivery in the app before the cut-off; empty
        fee = delivery off.
      </p>
      <Field label="Delivery fee (RM)">
        <Input
          inputMode="decimal"
          value={fee}
          disabled={!can}
          onChange={(e) => setFee(e.target.value)}
        />
      </Field>
      <ChipToggle
        label="Branches that deliver"
        options={settings.branches.map((b) => ({ value: b.id, label: b.name }))}
        selected={branchIds}
        onChange={(v) => can && setBranchIds(v)}
      />
      <Field label="Ask by (cut-off)">
        <Input
          type="time"
          value={cutoff}
          disabled={!can}
          onChange={(e) => setCutoff(e.target.value)}
        />
      </Field>
      <Field label="Delivered by">
        <Input
          type="time"
          value={deliverBy}
          disabled={!can}
          onChange={(e) => setDeliverBy(e.target.value)}
        />
      </Field>
      {can && (
        <Button variant="primary" loading={save.isPending} onClick={submit}>
          Save delivery settings
        </Button>
      )}
    </div>
  );
}
